// @flow
import * as React from 'react';
import { Route, Switch } from 'react-router-dom';
import asyncComponent from 'common/helpers/asyncComponent';
import SplashScreen from 'common/modules/ui/components/SplashScreen';
import type { FeaturePermission } from 'common/domain/FeaturePermission';
import { FeaturePermissions } from 'common/domain/FeaturePermission';

import { isDevFlagEnabled } from 'common/components/DevFlag';
import InvoicingPage from 'statsradio/modules/billing/InvoicingPage';

import * as StatsRadioRoutes from 'statsradio/helpers/constant/Routes';
import ResetPasswordPage from '../modules/authentication/resetPassword/StationResetPasswordPage';
import ForgotPasswordPage from '../modules/authentication/resetPassword/StationForgotPasswordPage';

import ConfirmEmailPage from '../modules/userProfile/components/ConfirmEmailPage';
import PrivateRouteComponent from '../modules/authentication/PrivateRoute';
import DefaultRouteRedirect from '../modules/authorisation/DefaultRouteRedirect';

import Permissions from '../modules/authorisation/Permissions';
import RadioConfigurationPageComponent from '../modules/radioConfiguration/RadioConfigurationPage';
import SalesPrintingPageComponent from '../modules/sales/SalesPrintingPage';
import SalesFlyerPrintingPageComponent from '../modules/sales/SalesFlyerPrintingPage';
import SalesPromotionsPageComponent from '../modules/promotions/SalesPromotionPage';
import TutorialPageComponent from '../modules/tutorials/TutorialPage';
import CustomersPageComponent from '../modules/customers/CustomersPage';
import CustomersPageComponentAdmin from '../modules/customers/CustomersPageAdmin';
import SalesPropositionPageComponent from '../modules/sales/SalesPropositionPage';
import SalesPropositionDetailsComponent from '../modules/sales/SalesPropositionDetailPage';
import CampaignDetailPageComponent from '../modules/campaigns/CampaignDetailPage';
import CampaignPrintingPageComponent from '../modules/campaigns/printing/CampaignPrintingPage';
import ReachPrintingPageComponent from '../modules/metrics/ReachPrintingPage';
import ListeningTimePrintingPageComponent from '../modules/metrics/ListeningTimePrintingPage';
import DashboardPrintingPageComponent from '../modules/dashboard/printing/DashboardPrintingPage';
import DemographyPrintingContainer from '../modules/demography/printing/DemographyPrintingPage';
import HistoryPageComponent from '../modules/history/HistoryPage';
import HistoryPrintingPageComponent from '../modules/history/printing/HistoryPrintingPage';
import SalesDashboardPage from '../modules/salesDashboard/SalesDashboardPage';
import CreateDemoCampaignForm from '../modules/campaigns/CreateDemoCampaignForm';
import AnomaliesBanner from '../modules/radio/components/AnomaliesBanner';
import StreamingPage from '../modules/streaming/StreamingPage';
import StreamDataPage from '../modules/streaming/StreamMetricsPage';
import DashboardPage from '../modules/dashboard/v2/DashboardPage';
import ReachPageV2 from '../modules/metrics/v2/ReachPage';

const DashboardPageComponent = asyncComponent(
  () => import('../modules/dashboard/DashboardPage'),
  <SplashScreen />,
);

const LoginPageComponent = asyncComponent(
  () => import('../modules/authentication/login/StatsRadioLoginPage'),
  <SplashScreen />,
);

const DemographyPageComponent = asyncComponent(
  () => import('../modules/demography/DemographyPage'),
  <SplashScreen />,
);

const SalesPageComponent = asyncComponent(
  () => import('../modules/sales/SalesPage'),
  <SplashScreen />,
);

const SalesFlyerPageComponent = asyncComponent(
  () => import('../modules/sales/SalesFlyerPage'),
  <SplashScreen />,
);

const UserProfilePageComponent = asyncComponent(
  () => import('../modules/userProfile/components/UserProfilePage'),
  <SplashScreen />,
);

const ReachPageComponent = asyncComponent(
  () => import('../modules/metrics/ReachPage'),
  <SplashScreen />,
);

const ListeningTimePageComponent = asyncComponent(
  () => import('../modules/metrics/ListeningTimePage'),
  <SplashScreen />,
);

const UserManagementPageComponent = asyncComponent(
  () => import('../modules/userManagement/UserManagementPage'),
  <SplashScreen />,
);

const AcceptInvitationPageComponent = asyncComponent(
  () => import('../modules/registration/AcceptInvitationPage'),
  <SplashScreen />,
);

const RadioSelectionPageComponent = asyncComponent(
  () => import('../modules/radio/RadioSelectionPage'),
  <SplashScreen />,
);

const SpotsPageComponent = asyncComponent(
  () => import('../modules/spots/SpotsPage'),
  <SplashScreen />,
);

const SpotInventoryPageComponent = asyncComponent(
  () => import('../modules/spots/SpotInventoryPage'),
  <SplashScreen />,
);

const NoPermissionsPageComponent = asyncComponent(
  () => import('../modules/authorisation/NoPermissionsPage'),
  <SplashScreen />,
);

const SpotSchedulePageComponent = asyncComponent(
  () => import('../modules/salesInventory/SpotSchedulePage'),
  <SplashScreen />,
);

const SurveyPage = asyncComponent(
  () => import('../modules/demography/SurveyPage'),
  <SplashScreen />,
);

const CampaignV2ListPage = asyncComponent(
  () => import('../modules/campaigns/CampaignsV2ListPage'),
  <SplashScreen />,
);

const CampaignV2ListPageAdmin = asyncComponent(
  () => import('../modules/campaigns/CampaignsV2ListPageAdmin'),
  <SplashScreen />,
);

interface Props {
  location: Object;
}

interface RouteProps {
  path: string;
  component: any;
}

interface PrivateRouteProps {
  path: string;
  component: any;
  featurePermission?: FeaturePermission | Array<FeaturePermission>;
  permission?: Permissions | Array<Permissions>;
  allowNotConfiguredRadio?: boolean;
  displayAnomaliesBanner?: boolean;
  hideForSmallMarket?: boolean;
}

class Routes extends React.PureComponent<Props> {
  withLocationPublicRoute() {
    return ({ path, component }: RouteProps) => (
      <Route path={path} component={component} location={this.props.location} />
    );
  }

  withLocationPrivateRoute() {
    return ({
      path,
      component,
      permission,
      featurePermission,
      allowNotConfiguredRadio,
      displayAnomaliesBanner,
      hideForSmallMarket,
    }: PrivateRouteProps) => (
      <React.Fragment>
        {displayAnomaliesBanner && <AnomaliesBanner />}
        <PrivateRouteComponent
          exact
          path={path}
          component={component}
          location={this.props.location}
          permission={permission}
          featurePermission={featurePermission}
          allowNotConfiguredRadio={allowNotConfiguredRadio}
          hideForSmallMarket={hideForSmallMarket}
        />
      </React.Fragment>
    );
  }

  render() {
    const PublicRoute = this.withLocationPublicRoute();
    const PrivateRoute = this.withLocationPrivateRoute();

    return (
      <Switch>
        <PublicRoute path="/reset-password" component={ResetPasswordPage} />
        <PublicRoute path="/forgot-password" component={ForgotPasswordPage} />
        <PublicRoute
          path="/accept-invitation"
          component={AcceptInvitationPageComponent}
        />
        <PublicRoute path="/confirm-email" component={ConfirmEmailPage} />
        <PublicRoute path="/login" component={LoginPageComponent} />
        <PublicRoute
          path="/proposition-printing"
          component={SalesPrintingPageComponent}
        />
        <PublicRoute
          path="/sales-flyer-printing"
          component={SalesFlyerPrintingPageComponent}
        />
        <PublicRoute
          path="/campaign-printing"
          component={CampaignPrintingPageComponent}
        />
        <PublicRoute
          path="/dashboard-printing"
          component={DashboardPrintingPageComponent}
        />
        <PublicRoute
          path="/reach-printing"
          component={ReachPrintingPageComponent}
        />
        <PublicRoute
          path="/listening-time-printing"
          component={ListeningTimePrintingPageComponent}
        />
        <PublicRoute
          path="/demography-printing"
          component={DemographyPrintingContainer}
        />
        <PublicRoute
          path="/history-printing"
          component={HistoryPrintingPageComponent}
        />

        <PrivateRoute
          path="/spot-inventory/spot-schedule"
          permission={Permissions.ACCESS_SPOTS}
          featurePermission={[
            FeaturePermissions.FOLLO_MODULE,
            FeaturePermissions.SALES_INVENTORY_MODULE,
          ]}
          component={SpotSchedulePageComponent}
        />
        <PrivateRoute
          path="/no-permissions"
          component={NoPermissionsPageComponent}
        />
        <PrivateRoute
          path="/select-radio"
          component={RadioSelectionPageComponent}
          allowNotConfiguredRadio
        />
        <PrivateRoute
          path="/configure-radio"
          component={RadioConfigurationPageComponent}
          allowNotConfiguredRadio
        />
        <PrivateRoute
          path="/dashboard"
          permission={Permissions.ACCESS_DASHBOARD}
          component={DashboardPageComponent}
          displayAnomaliesBanner
        />
        <PrivateRoute
          path="/dashboard-v2"
          permission={Permissions.ACCESS_DASHBOARD}
          component={DashboardPage}
          displayAnomaliesBanner
          hideForSmallMarket
        />
        <PrivateRoute
          path="/demography"
          permission={Permissions.ACCESS_DEMOGRAPHY}
          component={DemographyPageComponent}
          displayAnomaliesBanner
          hideForSmallMarket
        />
        <PrivateRoute
          path="/reach"
          permission={Permissions.ACCESS_REACH}
          component={ReachPageComponent}
          displayAnomaliesBanner
          hideForSmallMarket
        />
        <PrivateRoute
          path="/reach-v2"
          permission={Permissions.ACCESS_REACH}
          component={ReachPageV2}
          displayAnomaliesBanner
          hideForSmallMarket
        />
        <PrivateRoute
          path="/listening-time"
          permission={Permissions.ACCESS_LISTENING_TIME}
          component={ListeningTimePageComponent}
          displayAnomaliesBanner
          hideForSmallMarket
        />
        <PrivateRoute
          path="/history"
          permission={Permissions.ACCESS_HISTORY}
          component={HistoryPageComponent}
          displayAnomaliesBanner
        />
        <PrivateRoute
          path="/estimations/:reference"
          permission={Permissions.ACCESS_SALES}
          component={SalesPageComponent}
          displayAnomaliesBanner
        />
        <PrivateRoute
          path="/estimations"
          permission={Permissions.ACCESS_SALES}
          component={SalesPageComponent}
          displayAnomaliesBanner
        />
        <PrivateRoute
          path="/sales-flyer"
          permission={Permissions.ACCESS_SALES_FLYER}
          component={SalesFlyerPageComponent}
          displayAnomaliesBanner
        />
        <PrivateRoute
          path="/campaign/:reference"
          permission={Permissions.ACCESS_CAMPAIGNS}
          featurePermission={FeaturePermissions.FOLLO_MODULE}
          component={CampaignDetailPageComponent}
          displayAnomaliesBanner
        />

        <PrivateRoute
          path={StatsRadioRoutes.CAMPAIGNS}
          permission={Permissions.ACCESS_CAMPAIGNS}
          featurePermission={FeaturePermissions.FOLLO_MODULE}
          component={CampaignV2ListPage}
        />

        <PrivateRoute
          path={StatsRadioRoutes.ALLCAMPAIGNS}
          permission={Permissions.ACCESS_ALL_CAMPAIGNS}
          featurePermission={FeaturePermissions.FOLLO_MODULE}
          component={CampaignV2ListPageAdmin}
        />

        <PrivateRoute
          path="/customers"
          permission={Permissions.MANAGE_CUSTOMERS}
          featurePermission={FeaturePermissions.FOLLO_MODULE}
          component={CustomersPageComponent}
        />

        <PrivateRoute
          path="/all-customers"
          permission={Permissions.MANAGE_ALL_CUSTOMERS}
          featurePermission={FeaturePermissions.FOLLO_MODULE}
          component={CustomersPageComponentAdmin}
        />

        <PrivateRoute
          path="/promotions"
          component={SalesPromotionsPageComponent}
          featurePermission={[
            FeaturePermissions.FOLLO_MODULE,
            FeaturePermissions.SALES_INVENTORY_MODULE,
          ]}
        />
        <PrivateRoute
          path="/proposition/:reference"
          permission={Permissions.ACCESS_SALES}
          component={SalesPropositionDetailsComponent}
          featurePermission={FeaturePermissions.FOLLO_MODULE}
          displayAnomaliesBanner
        />
        <PrivateRoute
          path="/propositions"
          permission={Permissions.ACCESS_SALES}
          component={SalesPropositionPageComponent}
          featurePermission={FeaturePermissions.FOLLO_MODULE}
        />
        <PrivateRoute
          path="/spots/:campaignReference"
          permission={Permissions.ACCESS_SPOTS}
          component={SpotsPageComponent}
          featurePermission={FeaturePermissions.FOLLO_MODULE}
        />
        <PrivateRoute
          path="/spots"
          permission={Permissions.ACCESS_SPOTS}
          component={SpotsPageComponent}
        />
        <PrivateRoute
          path="/spot-inventory"
          permission={Permissions.ACCESS_SALES}
          component={SpotInventoryPageComponent}
          featurePermission={[
            FeaturePermissions.FOLLO_MODULE,
            FeaturePermissions.SALES_INVENTORY_MODULE,
          ]}
        />
        <PrivateRoute
          path="/user-management"
          permission={Permissions.MANAGE_USERS}
          component={UserManagementPageComponent}
          allowNotConfiguredRadio
        />
        <PrivateRoute
          path="/user-profile"
          component={UserProfilePageComponent}
          allowNotConfiguredRadio
        />
        <PrivateRoute
          path="/tutorials"
          component={TutorialPageComponent}
          allowNotConfiguredRadio
        />
        <PrivateRoute
          path="/create-demo-campaign"
          permission={Permissions.CREATE_DEMO_CAMPAIGN}
          component={CreateDemoCampaignForm}
        />
        <PrivateRoute
          path="/sales-dashboard"
          permission={Permissions.ACCESS_SALES}
          featurePermission={FeaturePermissions.FOLLO_MODULE}
          component={SalesDashboardPage}
        />
        {isDevFlagEnabled('QUALITATIVE') && (
          <PrivateRoute
            path="/survey"
            component={SurveyPage}
            featurePermission={FeaturePermissions.QUALITATIVE_MODULE}
          />
        )}

        <PrivateRoute
          path="/create-demo-campaign"
          permission={Permissions.CREATE_DEMO_CAMPAIGN}
          featurePermission={FeaturePermissions.FOLLO_MODULE}
          component={CreateDemoCampaignForm}
        />

        <PrivateRoute
          path={StatsRadioRoutes.INVOICING}
          permission={Permissions.BILL_RADIOS}
          component={InvoicingPage}
        />

        {isDevFlagEnabled('STREAMING_METRICS') && (
          <PrivateRoute
            path="/streams/:reference"
            permission={Permissions.ACCESS_STREAMING}
            component={StreamDataPage}
          />
        )}
        {isDevFlagEnabled('STREAMING_METRICS') && (
          <PrivateRoute
            path={StatsRadioRoutes.STREAMING}
            permission={Permissions.ACCESS_STREAMING}
            component={StreamingPage}
          />
        )}

        <Route path="/" component={DefaultRouteRedirect} />
      </Switch>
    );
  }
}

export default Routes;



// WEBPACK FOOTER //
// ./src/statsradio/layout/Routes.js